interface IConfigAuth {
  accessTokenUri: string;
  clientId: string;
  clientSecret: string;
}

interface IConfigSip {
  host: string;
  user: string;
  password: string;
  wsServers: string;
}

interface IConfigConnectServer {
  url: string;
}

interface IConfigInmate {
  itdsEnabled: boolean;
  housingUnitsEnabled: boolean;
  pinLength: number;
  purchaseItems?: IInmatePurchaseItem[];
}

interface IConfigVisitation {
  timeLimitOptions: number[];
  defaultTimeLimit: number;
}

interface IConfigSearch {
  defaultStartDatePeriod: number;
  outbound: IConfigSearchOutbound;
  inbound: IConfigSearchInbound;
  visitation: IConfigSearchVisitation;
}

interface IConfigSearchOutbound {
  defaultMinDuration: number;
  defaultMaxDuration?: number;
  startDatePeriod: number;
  criteriaChoices: string[];
  defaultCriteriaChoice: string;
  callTypes: { [key: string]: string };
  defaultCallTypes: string[];
}

interface IConfigSearchInbound {
  startDatePeriod: number;
  source: string;
}

interface IConfigSearchVisitation {
  startDatePeriod: number;
  defaultMinDuration: number;
  defaultMaxDuration?: number;
}

interface IEventNotification {
  preaReportTemplateIds?: {
    Email: number
  },
  textMessageAlertTemplateIds?: {
    Email: number
    Text: number
    Call: number
  }
}

interface IConfigApi {
  url: string;
}

interface IConfigPrivateNumber {
  readOnly: boolean;
}

export type Feature =
  | 'audio-call'
  | 'audio-visitation'
  | 'commissary'
  | 'inmate-requests'
  | 'job-search'
  | 'law-library'
  | 'reference-material'
  | 'texting'
  | 'video-call'
  | 'visitation'
  | 'voicemail'
  | 'wireless-audio-calls'
  | 'inmate-communication-policy'
  | 'transcription'
  | 'music';

export interface IConfig {
  name: string;
  timezone: string;
  nodeServer: string;
  auth: IConfigAuth;
  sip: IConfigSip;

  phpDateFormat: string;
  urlDateFormat: string;
  urlDateRegex: RegExp;
  connectServer?: IConfigConnectServer;
  inmate: IConfigInmate;
  visitation: IConfigVisitation;
  features?: Feature[];
  search: IConfigSearch;
  api: IConfigApi;
  centralApi: IConfigApi;
  privateNumber: IConfigPrivateNumber;
  eventNotification: IEventNotification;
}

export interface IInmatePurchaseItem {
  cost: number;
  title: string;
  transactionDescription: string;
}

const baseConfig: IConfig = {
  eventNotification: {}, //Leaving this null for now, unused at any facility but should be built to support,
  name: '', // We load this value upon loading a facility
  timezone: '', // We load this value upon loading a facility
  nodeServer: process.env.NODE_SERVER,
  api: {
    url: process.env.SITE_API_URL ?? (location.origin + '/api-site')
  },
  centralApi: {
    url: process.env.CENTRAL_API_URL
  },
  auth: {
    clientId: 'relcore-site',
    accessTokenUri: process.env.CENTRAL_API_URL,
    clientSecret: ''
  },
  features: [],
  sip: {
    host: 'window.location.hostname',
    // Username and password are unimportant since session ID header will be used for auth
    user: "rwis",
    password: "",
    wsServers: (location.protocol === 'https:' ? "wss://" : "ws://") + window.location.hostname + "/opensips"
  },
  connectServer: {
    url: null
  },
  phpDateFormat: 'YYYY-MM-DD HH:mm:ss',
  urlDateFormat: 'YYYYMMDD_HHmmss',
  urlDateRegex: /\d{8}_\d{6}/,
  inmate: {
    itdsEnabled: false, // Enable ITDS features
    housingUnitsEnabled: false,
    pinLength: 6,
    purchaseItems: [
      {cost:3, title: 'Headphones', transactionDescription: 'Replacement headphones'}
    ]
  },
  privateNumber: {
    readOnly: false
  },
  visitation: {
    timeLimitOptions: [15,30,60],
    defaultTimeLimit: 30
  },
  search: {
    defaultStartDatePeriod: -1,
    outbound: {
      defaultMinDuration: 0,
      defaultMaxDuration: null,
      startDatePeriod: 1,

      // Options: Destination, PhoneCard, Inmate
      criteriaChoices: ['Destination', 'PhoneCard', 'Inmate'],

      // One of the above criteriaChoices options
      defaultCriteriaChoice: 'Destination',

      callTypes: {
        'audio-call': 'Audio',
        'video-call': 'Video'
      },

      defaultCallTypes: [
        'audio-call',
        'video-call'
      ]
    },
    inbound: {
      startDatePeriod: 1,
      source: 'central'
    },
    visitation: {
      startDatePeriod: 1,
      defaultMinDuration: 60
    }
  }
}

interface IConfigService extends IConfig {
  hasFeature(feature: Feature): boolean;
  hasOneOfFeatures(features: Feature[]): boolean;
}

const config: IConfigService = {
  ...baseConfig,
  hasFeature(feature: Feature) {
    return (this.features || []).indexOf(feature) > -1;
  },
  hasOneOfFeatures(features: Feature[]) {
    return features.reduce((prev, cur) => prev || this.hasFeature(cur), false);
  }
};

export default config;
